import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { Search, Loader2, X, Users, ChevronDown } from 'lucide-react';
import { noAutofillProps } from '../../utils/noAutofill';

/**
 * Speaker profile selector for the speaker_bureau dashboard header.
 * Replaces the executive pulldown with a SEARCH field (scales to hundreds of
 * speakers via server-side /speakers?q=). The timeline is scoped to the selected
 * speaker; a "Show all programs" toggle reveals the whole org-shared pipeline.
 *
 * Props:
 *  - selectedSpeaker: { id, full_name, ... } | null
 *  - onSelect(speaker | null)
 *  - showAll: bool
 *  - onToggleShowAll(next: bool)
 */
const SpeakerProfileSelector = ({ selectedSpeaker, onSelect, showAll, onToggleShowAll }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  const name = (s) => (s && (s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim())) || '';

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Typeahead search
  useEffect(() => {
    if (!open) return;
    setSearching(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        const res = await axios.get(`${apiBase}/speakers`, {
          headers,
          params: { q: query.trim() || undefined, limit: 12 },
        });
        setResults(Array.isArray(res.data) ? res.data : []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  const pick = (s) => { onSelect && onSelect(s); setOpen(false); setQuery(''); };

  const inputCls = 'w-full bg-ui-hover/80 border border-ui-line rounded-lg pl-9 pr-3 py-1.5 text-base text-ui-ink placeholder-ui-muted focus:outline-none focus:ring-2 focus:ring-ui-blue-solid/50';
  const panelCls = 'absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border border-ui-line bg-ui-raised shadow-ui-pop';
  const rowCls = 'flex w-full items-center justify-between px-3 py-2 text-left text-sm text-ui-ink hover:bg-ui-hover';

  return (
    <div className="flex items-center gap-2" ref={boxRef}>
      <div className="relative flex-1 min-w-[220px]">
        {selectedSpeaker && !open ? (
          <button
            onClick={() => { setOpen(true); setQuery(''); }}
            className="flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-base font-bold bg-ui-hover/80 border border-ui-line text-ui-ink"
          >
            <span className="truncate">{name(selectedSpeaker)}</span>
            <ChevronDown className="ml-2 h-4 w-4 flex-shrink-0 text-ui-ink-3" />
          </button>
        ) : (
          <>
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ui-ink-3" />
            <input
              {...noAutofillProps}
              name="speaker-search"
              autoFocus={open}
              value={query}
              onFocus={() => setOpen(true)}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              placeholder="Search speakers..."
              className={inputCls}
            />
            {searching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-ui-ink-3" />}
          </>
        )}

        {open && (
          <div className={panelCls}>
            {results.length === 0 && !searching ? (
              <div className="px-3 py-3 text-sm text-ui-muted">No speakers found.</div>
            ) : (
              results.map((s) => (
                <button key={s.id} type="button" onClick={() => pick(s)} className={rowCls}>
                  <span className="truncate font-medium">{name(s)}</span>
                  {s.email && <span className="ml-2 truncate text-xs text-ui-muted">{s.email}</span>}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {selectedSpeaker && (
        <button
          onClick={() => onSelect && onSelect(null)}
          title="Clear"
          className="rounded-lg p-1.5 text-ui-ink-3 hover:bg-ui-hover"
        >
          <X className="h-4 w-4" />
        </button>
      )}

      <button
        onClick={() => onToggleShowAll && onToggleShowAll(!showAll)}
        className={`flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
          showAll
            ? 'bg-ui-pink-strong text-ui-on-primary'
            : 'bg-ui-hover/80 border border-ui-line text-ui-ink-2 hover:bg-ui-line'
        }`}
      >
        <Users className="h-3.5 w-3.5" />
        {showAll ? 'Showing all' : 'Show all'}
      </button>
    </div>
  );
};

export default SpeakerProfileSelector;
